import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, NgModule, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { CheckmarkLoaderModule } from '@commons/contact-modal/checkmark-loader/checkmark-loader.component';
import { ContactUsComponent } from '@core/components/contact-us/contact-us.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CompaniesRoutingEnum, SecureSpaceTabsEnum } from '@features/companies/companies-routing.enum';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { JobsRoutingEnum } from '@features/jobs/jobs-routing.enum';
import { Select } from '@ngxs/store';
import { ScrollToConfigOptions, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { SecureState } from '@stores/secure/secure.state';
import { WINDOW } from '@wizbii/angular-utilities';
import { RecipientEmailEnum } from '@wizbii/models';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

interface Social {
  label: string;
  icon: string;
  redirectUrl: SafeUrl;
  margin?: any;
}

@Component({
  selector: 'app-company-register-confirmation',
  templateUrl: './register-confirmation.component.html',
  styleUrls: ['./register-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyRegisterConfirmationComponent implements OnInit, OnDestroy {
  @Select(SecureState.companyId)
  companyId$!: Observable<string>;

  @Select(SecureState.secureSpaceVisited)
  secureSpaceVisited$!: Observable<string>;

  @Select(SecureState.companyToken)
  companyToken$!: Observable<string>;

  queryParamsCompanyToken$?: Observable<Record<string, string>>;

  loading$ = new BehaviorSubject<boolean>(true);

  socials$ = new BehaviorSubject<Social[] | undefined>(undefined);

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  RecipientEmailEnum = RecipientEmailEnum;
  FeaturesRoutingEnum = FeaturesRoutingEnum;
  JobsRoutingEnum = JobsRoutingEnum;
  CompaniesRoutingEnum = CompaniesRoutingEnum;
  trackEvent = trackEventWithAtInternet;

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly dialog: MatDialog,
    private readonly sanitizer: DomSanitizer,
    private readonly scrollToService: ScrollToService,
    @Inject(WINDOW) readonly window: any
  ) {}

  ngOnInit(): void {
    this.queryParamsCompanyToken$ = this.companyToken$.pipe(
      map((token) => ({ [SecureState.COMPANY_TOKEN]: token, tab: SecureSpaceTabsEnum.companyPage }))
    );

    this.window.setTimeout(() => {
      this.initShareLinks();

      const config: ScrollToConfigOptions = {
        target: 'confirmation-title',
      };

      this.scrollToService.scrollTo(config);
    });

    this.window.setTimeout(() => {
      this.loading$.next(false);
    }, 1000);
  }

  openContactForm(params?: { recipientEmail?: string; templateKey?: string; trackEventAction?: string }): void {
    if (params?.trackEventAction) {
      this.trackEvent({ category: 'Formulaire Engagement', action: params.trackEventAction, type: 'action' });
    }

    this.isMobile$.pipe(take(1)).subscribe({
      next: (isMobile) => {
        this.dialog.open(ContactUsComponent, {
          panelClass: isMobile ? 'wizbii-mobile-panel--white' : 'wizbii-panel--white',
          backdropClass: 'wzb-backdrop',
          minWidth: isMobile ? '100vw' : '50vw',
          autoFocus: false,
          data: {
            isMobile,
            recipientEmail: params?.recipientEmail,
            templateKey: params?.templateKey,
          },
          disableClose: isMobile,
          position: isMobile ? { bottom: '0' } : {},
        });
      },
    });
  }

  initShareLinks(): void {
    const currentUrl = this.window.location.origin;
    const message =
      "Mon entreprise s'engage pour l'emploi des jeunes ! Retrouvez nos engagements et rejoignez vous aussi la mobilisation « Les entreprises s'engagent » sur 1jeune1solution.gouv.fr/entreprises #1jeune1solution #FranceRelance";

    const facebookShareUrl = this.sanitizer.bypassSecurityTrustUrl(
      `https://www.facebook.com/sharer/sharer.php?mode=message&u=${currentUrl}&quote=${message}`
    );

    const twitterShareUrl = this.sanitizer.bypassSecurityTrustUrl(
      `https://twitter.com/intent/tweet?text=${message}&url=${currentUrl}`
    );

    const linkedinShareUrl = this.sanitizer.bypassSecurityTrustUrl(
      `https://www.linkedin.com/shareArticle?url=${currentUrl}&title=${message}`
    );

    // tslint:disable: no-duplicate-string
    this.socials$.next([
      {
        label: 'Linkedin',
        icon: 'linkedin_symbol',
        redirectUrl: linkedinShareUrl,
        margin: { 'margin-left': '8px' },
      },
      {
        label: 'Facebook',
        icon: 'facebook_symbol',
        margin: { 'margin-left': '15px' },
        redirectUrl: facebookShareUrl,
      },
      {
        label: 'Twitter',
        icon: 'twitter_symbol',
        redirectUrl: twitterShareUrl,
        margin: { 'margin-top': '5px', 'margin-left': '3px' },
      },
    ]);
  }

  trackBySocials(_: number, social: Social): string {
    return social.label;
  }

  ngOnDestroy(): void {
    this.socials$.complete();
    this.loading$.complete();
  }
}

@NgModule({
  imports: [CommonModule, CheckmarkLoaderModule, RouterModule, MatIconModule],
  declarations: [CompanyRegisterConfirmationComponent],
  exports: [CompanyRegisterConfirmationComponent],
})
export class CompanyRegisterConfirmationModule {}
